import { addDays, startOfDay } from "@app/core";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSettingsStore, useTasksStore } from "../../stores/index.js";
import { useCalendarNavStore } from "../../stores/calendarNav.store.js";
import { useViewFilters } from "../../lib/useViewFilters.js";
import { useExpandedWindow } from "../../features/calendar-view/useExpandedWindow.js";
import { DayGrid } from "../../features/calendar-view/DayGrid.js";
import { CalendarHeader } from "../../features/calendar-view/CalendarHeader.js";
import { shiftAnchor } from "../../features/calendar-view/shift-anchor.js";
import { InboxPanel, InboxToggle } from "../../features/inbox/index.js";
import { MobileInboxDrawer } from "../../features/mobile/index.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { useCalendarNav, useRememberedCalendarAnchor } from "./useCalendarNav.js";

export function DayRoute() {
  const [anchor, setAnchor] = useRememberedCalendarAnchor("day", () => new Date());
  const [inboxOpen, setInboxOpen] = useState(false);
  const mobile = useMobileUx();
  const tasks = useTasksStore((s) => s.tasks);
  const rules = useTasksStore((s) => s.rules);
  const overrides = useTasksStore((s) => s.overrides);
  const loaded = useTasksStore((s) => s.loaded);
  const hydrate = useTasksStore((s) => s.hydrate);
  const applyFilters = useViewFilters();
  const setDayCount = useSettingsStore((s) => s.setCalendarDayCount);
  const navigate = useNavigate();
  const setStep = useCalendarNavStore((s) => s.setStep);
  useEffect(() => { if (!loaded) void hydrate(); }, [loaded, hydrate]);
  useEffect(() => {
    setStep((delta) => setAnchor((prev) => shiftAnchor(prev, "day", delta)));
    return () => setStep(null);
  }, [setStep]);
  useCalendarNav("day", setAnchor);

  const visibleStart = useMemo(() => startOfDay(anchor), [anchor]);
  const filtered = useMemo(() => applyFilters(tasks), [applyFilters, tasks]);
  const { days, windowStart } = useExpandedWindow(visibleStart, 1, filtered, rules, overrides);

  function handleDayCountChange(delta: number) {
    void setDayCount(1 + delta);
    navigate("/calendar/days");
  }

  return (
    <div className={`flex h-full min-h-0 flex-col ${mobile ? "" : "gap-4"}`}>
      <CalendarHeader
        anchor={anchor}
        onChange={setAnchor}
        unit="day"
        actions={<InboxToggle open={inboxOpen} onToggle={() => setInboxOpen((v) => !v)} />}
      />
      <div className="flex min-h-0 flex-1 gap-4">
        <div className="min-w-0 flex-1">
          <DayGrid
            days={days}
            windowStart={windowStart}
            visibleStart={visibleStart}
            onDayShift={(delta) => setAnchor((prev) => addDays(prev, delta))}
            onDayCountChange={handleDayCountChange}
          />
        </div>
        {inboxOpen && !mobile && (
          <aside className="w-64 shrink-0 rounded-md border bg-card p-2">
            <InboxPanel />
          </aside>
        )}
      </div>
      {mobile && <MobileInboxDrawer open={inboxOpen} onOpenChange={setInboxOpen} />}
    </div>
  );
}
